import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Optional Zelle enrollment fields (create/edit participant).
 * Values match Zelle QR JSON: token + name → enroll.zellepay.com link at settlement time.
 */
export default function ZellePaymentSection({
  idBase = "zelle",
  enabled,
  onEnabledChange,
  zelleUsername,
  setZelleUsername,
  zelleRecipientName,
  setZelleRecipientName,
  onValidationChange,
}) {
  const { t } = useTranslation();

  const toggleId = useMemo(() => `${idBase}-toggle`, [idBase]);

  const handleEnableChange = useCallback(
    (next) => {
      if (typeof onEnabledChange === "function") onEnabledChange(next);
      if (!next) {
        setZelleUsername("");
        setZelleRecipientName("");
        if (typeof onValidationChange === "function") {
          onValidationChange(true);
        }
      }
    },
    [onEnabledChange, setZelleUsername, setZelleRecipientName, onValidationChange]
  );

  useEffect(() => {
    if (!enabled) {
      if (typeof onValidationChange === "function") onValidationChange(true);
      return;
    }
    const ok =
      !!String(zelleUsername || "").trim() && !!String(zelleRecipientName || "").trim();
    if (typeof onValidationChange === "function") onValidationChange(ok);
  }, [enabled, zelleUsername, zelleRecipientName, onValidationChange]);

  const isZelleOk = useMemo(() => {
    if (!enabled) return false;
    return !!String(zelleUsername || "").trim() && !!String(zelleRecipientName || "").trim();
  }, [enabled, zelleUsername, zelleRecipientName]);

  return (
    <div className="mt-3">
      <div className="as-optional-block">
        <div className="form-check form-switch as-optional-toggle">
          <input
            className="form-check-input"
            type="checkbox"
            id={toggleId}
            checked={!!enabled}
            onChange={(e) => handleEnableChange(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={toggleId}>
            <span className="as-field-label-badge" aria-hidden="true">
              💜
            </span>
            {t("participant.add_zelle_optional")}
            {isZelleOk ? (
              <span
                className="ms-2 text-success fw-bold"
                aria-label={t("participant.zelle_ok")}
                title={t("participant.zelle_ok")}
              >
                ✅
              </span>
            ) : null}
          </label>
        </div>

        {enabled ? (
          <div className="mt-2">
            <label className="form-label small text-muted mb-1" htmlFor={`${idBase}-username`}>
              {t("participant.zelle_username_label")}
            </label>
            <input
              id={`${idBase}-username`}
              type="text"
              className="form-control as-zelle-input"
              value={zelleUsername}
              onChange={(e) => setZelleUsername(e.target.value)}
              placeholder={t("participant.zelle_username_placeholder")}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              inputMode="email"
            />
            <label
              className="form-label small text-muted mb-1 mt-2"
              htmlFor={`${idBase}-recipient`}
            >
              {t("participant.zelle_recipient_name_label")}
            </label>
            <input
              id={`${idBase}-recipient`}
              type="text"
              className="form-control as-zelle-input"
              value={zelleRecipientName}
              onChange={(e) => setZelleRecipientName(e.target.value)}
              placeholder={t("participant.zelle_recipient_name_placeholder")}
              spellCheck={false}
              autoCapitalize="words"
            />
            <div className="form-text mt-2">{t("participant.zelle_body_hint")}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
